import React, { useEffect, useState } from 'react';

type Props = {
  /** Image URL. If missing or it 404s, `fallback` is rendered instead. */
  src?: string | null;
  /** Passed through to the underlying `<img>` when it does render. */
  alt?: string;
  /** `className` applied to the `<img>` element itself (not the fallback). */
  imgClassName?: string;
  /**
   * Rendered in place of the `<img>` when `src` is empty, null, or the
   * browser fires `onError` while loading. Usually a colored chip with the
   * entity's initials, or a Heroicon in a tinted square. If omitted, the
   * component renders nothing on failure — useful for decorative hero
   * images that can simply disappear if unavailable.
   */
  fallback?: React.ReactNode;
};

/**
 * Gracefully degrades to a fallback when an image URL is missing or fails
 * to load. Prevents the browser's native broken-image glyph from leaking
 * into the UI.
 */
const FallbackImage: React.FC<Props> = ({ src, alt = '', imgClassName, fallback }) => {
  const [errored, setErrored] = useState(false);

  // Reset error state when the src changes (e.g. switching between clinics
  // in a list) so a previously-failed URL doesn't poison a new, valid one.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  if (!src || errored) {
    return <>{fallback}</>;
  }

  return <img src={src} alt={alt} className={imgClassName} onError={() => setErrored(true)} />;
};

export default FallbackImage;
